import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Container } from 'reactstrap';
import Parse, { attributesToProps, domToReact } from 'html-react-parser';
import { createUsePuck, registerOverlayPortal } from '@puckeditor/core';
import tinymce from 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/table';
import 'tinymce/plugins/media';
import 'tinymce/plugins/code';
import 'tinymce/skins/ui/oxide/skin.min.css';
import { Editor } from '@tinymce/tinymce-react';

import formatIntl from './Common/formatIntl';
import { withLayout } from './PuckLayout';

if (typeof window !== 'undefined' && !window.tinymce) {
  window.tinymce = tinymce;
}

const usePuckSelector = createUsePuck();

const replace = (node) => {
  if (node.name === 'div' && node.attribs) {
    const properties = attributesToProps(node.attribs);
    if (properties.className) {
      properties.className = '';
    }
    return (
      <div {...properties}>
        {domToReact(node.children)}
      </div>
    );
  }
  return node;
};

const buildTinymceInit = (toolbarContainerSelector) => ({
  inline: true,
  fixed_toolbar_container: toolbarContainerSelector,
  ui_container: document.body,
  toolbar_persist: true,
  menubar: false,
  skin: false,
  content_css: false,
  browser_spellcheck: true,
  relative_urls: false,
  remove_script_host: false,
  content_style: '.tmcee { font-family: \'Source Sans Pro\', sans-serif; } .tmcee h1, .tmcee h2, .tmcee h3, .tmcee h4, .tmcee h5, .tmcee h6 { font-weight: 500; } .tmcee iframe, .tmcee .mce-object-iframe { width: 100%; height: 100%; } .mce-content-body { margin-top: 4px; display: flow-root; }',
  plugins: ['lists', 'link', 'preview', 'table', 'media', 'code'],
  toolbar:
    'undo redo | blocks | bold italic forecolor |' +
    'alignleft aligncenter alignright alignjustify link |' +
    'bullist numlist outdent indent | table | media code',
});

const InlineHTMLEditor = ({ html, id }) => {
  const dispatch = usePuckSelector((s) => s.dispatch);
  const getSelectorForId = usePuckSelector((s) => s.getSelectorForId);
  const getItemById = usePuckSelector((s) => s.getItemById);

  const currentItemSelectorFromStore = usePuckSelector((s) => s.appState.ui.itemSelector);
  const currentItemSelectorRef = useRef(currentItemSelectorFromStore);
  useEffect(() => {
    currentItemSelectorRef.current = currentItemSelectorFromStore;
  }, [currentItemSelectorFromStore]);

  const containerRef = useRef(null);
  const editableAreaRef = useRef(null);
  const toolbarRef = useRef(null);
  const editorRef = useRef(null);
  const isEditorFocusedRef = useRef(false);
  const isDialogOpenRef = useRef(false);
  const movedSinkRef = useRef(null);
  const lastCommittedHtmlRef = useRef(typeof html === 'string' ? html : '');
  const lastKnownHtmlRef = useRef(typeof html === 'string' ? html : '');
  const selectorSnapshotRef = useRef({ zone: null, index: null });
  const positionSelector = getSelectorForId(id);

  if (positionSelector && typeof positionSelector.index === 'number') {
    selectorSnapshotRef.current = {
      zone: positionSelector.zone || '',
      index: positionSelector.index,
    };
  }

  const editorMountKey = id;
  const editorDomId = `puck-html-editor-${id}`;
  const toolbarDomId = `puck-html-editor-toolbar-${id}`;
  const editorInit = useMemo(
    () => buildTinymceInit(`#${toolbarDomId}`),
    [toolbarDomId],
  );

  const syncEditorContent = useCallback((nextHtml) => {
    const editor = editorRef.current;
    if (!editor || editor.removed) {
      return;
    }

    const normalized = typeof nextHtml === 'string' ? nextHtml : '';
    lastKnownHtmlRef.current = normalized;
    const current = editor.getContent({ format: 'html' });
    if (current !== normalized) {
      editor.setContent(normalized);
    }

    editor.nodeChanged();
  }, []);

  const commitValueToPuck = useCallback((nextHtml) => {
    const normalized = typeof nextHtml === 'string' ? nextHtml : '';
    if (normalized === lastCommittedHtmlRef.current) {
      return;
    }

    const selector = getSelectorForId(id) || selectorSnapshotRef.current;
    if (!selector) return;
    const item = getItemById(id);
    if (!item) return;

    try {
      dispatch({
        type: 'replace',
        destinationZone: selector.zone,
        destinationIndex: selector.index,
        data: { ...item, props: { ...item.props, html: normalized } },
      });
      lastCommittedHtmlRef.current = normalized;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        'PuckHTML: failed to commit HTML content to Puck store, will retry on next commit trigger.',
        error,
      );
    }
  }, [dispatch, getItemById, getSelectorForId, id]);

  const flushPendingCommit = useCallback(() => {
    commitValueToPuck(lastKnownHtmlRef.current);
  }, [commitValueToPuck]);

  const deferredFlushPendingCommit = useCallback(() => {
    window.setTimeout(flushPendingCommit, 0);
  }, [flushPendingCommit]);

  const selectCurrentItemInSidebar = useCallback(() => {
    const selector = getSelectorForId(id) || selectorSnapshotRef.current;
    if (!selector) return;

    const { current } = currentItemSelectorRef;
    const isAlreadySelected = current
      && current.zone === selector.zone
      && current.index === selector.index;

    if (isAlreadySelected) {
      return;
    }

    window.setTimeout(() => {
      dispatch({
        type: 'setUi',
        ui: {
          itemSelector: {
            index: selector.index,
            zone: selector.zone,
          },
        },
      });
    }, 0);
  }, [dispatch, getSelectorForId, id]);

  useEffect(() => {
    if (!isEditorFocusedRef.current) {
      return;
    }

    const selector = getSelectorForId(id) || selectorSnapshotRef.current;
    if (!selector) {
      return;
    }

    const matchesUs = currentItemSelectorFromStore
      && currentItemSelectorFromStore.zone === selector.zone
      && currentItemSelectorFromStore.index === selector.index;

    if (!matchesUs) {
      selectCurrentItemInSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItemSelectorFromStore]);

  useEffect(() => {
    if (!editableAreaRef.current) {
      return () => {};
    }

    const cleanup = registerOverlayPortal(editableAreaRef.current, { disableDrag: true });
    return cleanup;
  }, [id]);

  useEffect(() => {
    if (!toolbarRef.current) {
      return () => {};
    }

    const cleanup = registerOverlayPortal(toolbarRef.current, { disableDrag: true });
    return cleanup;
  }, [id]);

  useEffect(() => {
    const toolbarNode = toolbarRef.current;
    if (!toolbarNode) {
      return () => {};
    }

    const relocateSink = (sink) => {
      if (sink && sink.parentElement !== document.body) {
        document.body.appendChild(sink);
        movedSinkRef.current = sink;
      }
    };

    const existingSink = toolbarNode.querySelector('.tox-silver-sink');
    if (existingSink) {
      relocateSink(existingSink);
    }

    const findSinkInAddedNodes = (mutations) => mutations
      .flatMap((mutation) => Array.from(mutation.addedNodes))
      .filter((node) => node.nodeType === 1)
      .reduce((found, node) => {
        if (found) return found;
        if (node.classList?.contains('tox-silver-sink')) return node;
        return node.querySelector?.('.tox-silver-sink') || null;
      }, null);

    const observer = new MutationObserver((mutations) => {
      if (movedSinkRef.current) return;
      const sink = findSinkInAddedNodes(mutations);
      if (sink) {
        relocateSink(sink);
      }
    });

    observer.observe(toolbarNode, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (movedSinkRef.current && movedSinkRef.current.parentElement === document.body) {
        movedSinkRef.current.remove();
      }
      movedSinkRef.current = null;
    };
  }, [id]);

  useEffect(() => {
    const rafId = window.requestAnimationFrame(() => {
      const editor = editorRef.current;
      if ((editor && !editor.removed && editor.hasFocus()) || isEditorFocusedRef.current) {
        return;
      }

      const normalizedHtml = typeof html === 'string' ? html : '';
      if (normalizedHtml !== lastKnownHtmlRef.current) {
        lastCommittedHtmlRef.current = normalizedHtml;
        syncEditorContent(normalizedHtml);
      }
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [id, html, syncEditorContent]);

  useEffect(() => () => {
    commitValueToPuck(lastKnownHtmlRef.current);
  }, [commitValueToPuck]);

  const handleChange = (value) => {
    const normalizedValue = typeof value === 'string' ? value : '';
    lastKnownHtmlRef.current = normalizedValue;
  };

  return (
    <div className="puck-html-editor-host tmcee" ref={containerRef}>
      <div className="puck-html-editor-toolbar-wrap">
        <span className="puck-html-editor-drag-handle" title="Drag to move section" aria-hidden="true">
          <i className="fal fa-grip-dots-vertical" />
        </span>
        <div id={toolbarDomId} ref={toolbarRef} className="puck-html-editor-toolbar" />
      </div>
      <div className="puck-html-editor-editable-area" ref={editableAreaRef}>
        <Editor
          id={editorDomId}
          key={editorMountKey}
          initialValue={lastKnownHtmlRef.current}
          init={editorInit}
          onInit={(evt, editor) => {
            editorRef.current = editor;
            syncEditorContent(lastKnownHtmlRef.current);

            editor.on('focus', () => {
              isEditorFocusedRef.current = true;
              selectCurrentItemInSidebar();
            });

            editor.on('mousedown', () => {
              isEditorFocusedRef.current = true;
              selectCurrentItemInSidebar();
            });

            editor.on('click', () => {
              isEditorFocusedRef.current = true;
              selectCurrentItemInSidebar();
            });

            editor.on('OpenWindow', () => {
              isDialogOpenRef.current = true;
            });

            editor.on('CloseWindow', () => {
              isDialogOpenRef.current = false;
              window.setTimeout(() => {
                if (!editor.hasFocus()) {
                  isEditorFocusedRef.current = false;
                  deferredFlushPendingCommit();
                }
              }, 0);
            });

            editor.on('blur', () => {
              if (isDialogOpenRef.current) return;
              isEditorFocusedRef.current = false;
              deferredFlushPendingCommit();
            });
          }}
          onEditorChange={handleChange}
        />
      </div>
    </div>
  );
};

InlineHTMLEditor.propTypes = {
  html: PropTypes.string,
  id: PropTypes.string,
};

InlineHTMLEditor.defaultProps = {
  html: '',
  id: '',
};

const PuckHTML = {
  fields: {
    container: {
      type: 'radio',
      get label() { return formatIntl('Width'); },
      options: [
        { get label() { return formatIntl('Container'); }, value: 'container' },
        { get label() { return formatIntl('Full width'); }, value: 'full' },
      ],
    },
  },
  get defaultProps() {
    return {
      html: '<h1>Heading</h1><p>Paragraph</p>',
      container: 'container',
    };
  },
  render: ({
    html, id, puck, container,
  }) => {
    let content = null;

    if (puck.isEditing) {
      content = <InlineHTMLEditor html={html} id={id} />;
    } else if (html?.trim()) {
      content = Parse(html, { trim: true, replace }) || <div />;
    }

    if (container === 'container') {
      return (
        <Container>
          <div className={classnames('landing-html', { 'custom-page-section': !puck.isEditing })}>
            {content}
          </div>
        </Container>
      );
    }

    return content;
  },
};

export default withLayout(PuckHTML);
